/**
 * The master category list, and every view of it the rest of Prism uses.
 *
 * `categories.json` is the only place a category is defined. Workers, the
 * frontend, the pricing prompt, Testing and the listing generator all import
 * from here, so adding or moving a category is one edit. Spec:
 * docs/specs_20260924_category_master_list.md.
 *
 * The exports keep the names the old hand-written copies used, so switching a
 * consumer over is an import change, not a rewrite.
 */

import CATEGORY_DATA from './categories.json';

const SUB_BY_NAME = new Map(CATEGORY_DATA.subs.map((sub) => [sub.name, sub]));

/**
 * The catch-all top level for commercial and install AV. Only valid with one of its
 * named subs: a bare "Other" is a guess, and every consumer refuses it.
 */
export const OTHER_MAIN_CATEGORY = 'Other';

/** Top-level categories in menu order. */
export const CATEGORY_ORDER = CATEGORY_DATA.mains.map((main) => main.name);

/** Alias kept for the frontend, which called this list CATEGORY_GROUP_ORDER. */
export const CATEGORY_GROUP_ORDER = CATEGORY_ORDER;

/** Each top level with its subs in picker order. A shared sub may appear under more than one. */
export const CATEGORY_GROUPS = Object.fromEntries(
    CATEGORY_DATA.mains.map((main) => [main.name, main.subs]),
);

/** Subs that carry more than one `main:` tag, keyed by sub. */
export const SHARED_CATEGORIES = Object.fromEntries(
    CATEGORY_DATA.subs.filter((sub) => sub.mains.length > 1).map((sub) => [sub.name, sub.mains]),
);

/**
 * Each top level with every sub tagged under it, i.e. every sub whose products
 * carry that `main:` tag. Use this to filter by top level: it includes shared
 * subs that the pickers only list under their other top level.
 */
export const SUBS_BY_MAIN = Object.fromEntries(
    CATEGORY_DATA.mains.map((main) => [
        main.name,
        CATEGORY_DATA.subs.filter((sub) => sub.mains.includes(main.name)).map((sub) => sub.name),
    ]),
);

/** Every sub as listed in the pickers, sorted. A sub listed under two top levels appears twice. */
export const PRODUCT_TYPES = Object.values(CATEGORY_GROUPS).flat().sort();

/** The pricing worker's shape: one row per top level, subs in picker order. */
export const TAXONOMY = CATEGORY_DATA.mains.map((main) => ({ main: main.name, subs: main.subs }));

export const MAIN_CATEGORIES = CATEGORY_ORDER;

/** Singular noun per sub, for listing copy ("a bass guitar"). */
export const CANONICAL_NOUN_SINGULAR = Object.fromEntries(
    CATEGORY_DATA.subs.map((sub) => [sub.name, sub.listingNoun]),
);

/** Observed drift with one clear home, keyed by the normalised raw value. */
export const SUB_ALIASES = new Map(
    CATEGORY_DATA.subs.flatMap((sub) => (sub.aliases ?? []).map((alias) => [alias, sub.name])),
);

/** Raw values that name a top level but not a sub, keyed by the normalised raw value. */
export const MAIN_ALIASES = new Map(Object.entries(CATEGORY_DATA.mainAliases));

/** Testing checklist name per sub. */
export const TESTING_BUCKET_BY_SUB = Object.fromEntries(
    CATEGORY_DATA.subs.map((sub) => [sub.name, sub.testingBucket]),
);

/** Collection handle per sub, as live on the site. Five pedal handles predate the "Pedals" suffix. */
export const COLLECTION_HANDLE_BY_SUB = Object.fromEntries(
    CATEGORY_DATA.subs.map((sub) => [sub.name, sub.handle]),
);

/**
 * Top-level categories for a product type (a sub name).
 *
 * Usually one, two for a shared sub. An empty or missing product type is the
 * handled "uncategorised" state and returns [] quietly. A non-empty value the
 * list does not know is a real bug upstream, so it is logged for Sentry.
 */
export function getMainCategories(productType) {
    if (productType === null || productType === undefined || productType === '') {
        return [];
    }
    const sub = SUB_BY_NAME.get(productType);
    if (sub) {
        return sub.mains;
    }
    console.error(`getMainCategories: unknown product type "${productType}". Caller will emit no main: tag. Canonicaliser upstream should have caught this.`);
    return [];
}

/** True only when `sub` is listed under `main` in the pickers. */
export function isValidCategory(main, sub) {
    return Boolean(CATEGORY_GROUPS[main]?.includes(sub));
}

/** Category rules for the classifier prompts, one "- rule" line each. Both the vision prompt and the fallback classifier embed this block. */
export function renderClassifierRules() {
    return CATEGORY_DATA.classifierRules.map((rule) => `- ${rule}`).join('\n');
}

/** "- Main: Sub1, Sub2" per top level. The vision prompt embeds this block verbatim. */
export function renderTaxonomyLines() {
    return TAXONOMY.map((row) => `- ${row.main}: ${row.subs.join(', ')}`).join('\n');
}

/** Normalised key for matching a raw category string: case, punctuation, "&" = "and". */
export const normaliseCategoryName = (s) =>
    String(s ?? '').toLowerCase().replace(/&/g, ' and ').replace(/[^a-z0-9]+/g, ' ').trim();

const SUB_BY_NORMALISED = new Map(CATEGORY_DATA.subs.map((sub) => [normaliseCategoryName(sub.name), sub.name]));

/** The master sub a raw string names (exact, missing plural "s", or alias), or null. */
export function canonicalSubFor(raw) {
    const key = normaliseCategoryName(raw);
    if (!key) return null;
    return SUB_BY_NORMALISED.get(key) ?? SUB_BY_NORMALISED.get(`${key}s`) ?? SUB_ALIASES.get(key) ?? null;
}

const subNamesWhere = (test) => CATEGORY_DATA.subs.filter(test).map((sub) => sub.name);

// Subs sold as a stereo pair: quotes confirm pair and quantity before an offer.
export const PAIRABLE_SUBS = new Set(subNamesWhere((sub) => sub.soldInPairs));

// Plural listing noun for the subs sold as a pair ("Hi-Fi speakers"), for "This pair of ...".
export const LISTING_NOUN_PLURAL = Object.fromEntries(
    CATEGORY_DATA.subs.filter((sub) => sub.listingNounPlural).map((sub) => [sub.name, sub.listingNounPlural]),
);

// Listing titles: subs that stay plural for one item ("Headphones").
export const TITLE_TYPE_INVARIANT = new Set(subNamesWhere((sub) => sub.titlePlural === 'always'));

// Listing titles: subs that stay plural only when the item is a pair or set.
export const TITLE_TYPE_COUNT_DEPENDENT = new Set(subNamesWhere((sub) => sub.titlePlural === 'ifPair'));

// Broken guitars use the lower "(To Repair?)" price threshold.
export const GUITAR_TYPES_FOR_TITLE = subNamesWhere((sub) => sub.guitarRepairRule);

export { CATEGORY_DATA };
